import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';
import { isTokenExpired } from '@/utils';
import { isPrivateMode, isPublicMode, isBothMode } from '@/utils/app-mode';
import { isCbContext, isAppContext, isEmbeddedContext, shouldRewriteAppRouteToCb } from './router-helper';
import { lazyComponents } from './lazy-components';
import { createThankYouGuard } from './route-guards';
import { appPatientRoutes, cbPatientRoutes, embeddedPatientRoutes } from './patient-routes';
import AppLayout from '@/layouts/AppLayout.vue';
import AppLayoutEmbedded from '@/layouts/AppLayoutEmbedded.vue';
import AppLayoutPlans from '@/layouts/AppLayoutPlans.vue';

Vue.use(VueRouter);

const PRIVATE_PUBLIC_ROUTE_NAMES = ['login', 'recover-password', 'register-client'];

const cbBackofficeRoutes = [
  {
    path: 'login',
    name: 'login',
    component: lazyComponents.plansLogin,
  },
  {
    path: 'recuperar-senha',
    name: 'recover-password',
    component: lazyComponents.plansRecover,
  },
  {
    path: 'cadastrar',
    name: 'register-client',
    component: lazyComponents.plansRegister,
  },
  {
    path: 'painel',
    name: 'backoffice-home',
    component: lazyComponents.plansBackofficeHome,
  },
  {
    path: 'editar-perfil',
    name: 'backoffice-edit-profile',
    component: lazyComponents.plansEditProfile,
  },
  {
    path: 'historico-consultas',
    name: 'schedules-history',
    component: lazyComponents.plansScheduleHistory,
  },
  {
    path: 'meu-plano',
    name: 'backoffice-plan',
    component: lazyComponents.plansMyPlan,
  },
  {
    path: 'pagamentos',
    name: 'backoffice-payments',
    component: lazyComponents.plansPaymentHistory,
  },
];

const appOnlyRoutes = [
  {
    path: 'auto-identificacao/:token',
    name: 'auto-id-exercito',
    redirect: (to) => {
      const { token } = to.params;
      return {
        name: 'schedule-start',
        query: { token },
      };
    },
  },
  {
    path: 'reagendamento-teste/:cpf',
    name: 'reschedule-test',
    component: lazyComponents.reschedule,
  },
  {
    path: 'meu-agendamento-teste/:cpf',
    name: 'scheduled-start-test',
    component: lazyComponents.scheduledStart,
  },
];

const embeddedOnlyRoutes = [
  {
    path: 'agradecimento',
    name: 'embedded-thanks',
    component: lazyComponents.thankYou,
  },
];

const routes = [
  {
    path: '*',
    name: 'schedule',
    redirect: { name: 'error', params: { code: 404 } },
  },
  {
    path: '/',
    name: 'home',
    redirect: { name: isPrivateMode() && !isBothMode() ? 'login' : 'schedule-start' },
  },
  {
    path: '/agradecimento',
    name: 'thank-you',
    component: lazyComponents.thankYou,
    beforeEnter: createThankYouGuard(),
  },
  {
    path: '/erro/:code',
    name: 'error',
    component: lazyComponents.notFound,
  },
  {
    path: '/cb',
    component: AppLayoutPlans,
    children: [...cbBackofficeRoutes, ...cbPatientRoutes],
  },
  {
    path: '/int',
    component: AppLayoutEmbedded,
    children: [...embeddedPatientRoutes, ...embeddedOnlyRoutes],
  },
  {
    path: '/app',
    component: AppLayout,
    children: [...appPatientRoutes, ...appOnlyRoutes],
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return {
      x: 0,
      y: 0,
    };
  },
});

const handleRefreshToken = async (token, user, next) => {
  try {
    await store.dispatch('plans-auth/refreshToken', {
      token: token,
      username: user.username,
    });
    return next();
  } catch {
    store.commit('plans-auth/CLEAR_LOGGED_USER');
    return next({ path: '/cb/login', query: { sessionExpired: '1' } });
  }
};

const getUserData = () => {
  try {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    return user;
  } catch {
    store.commit('plans-auth/CLEAR_LOGGED_USER');
    return null;
  }
};

router.beforeEach(async (to, from, next) => {
  // Mode-based route isolation — BOTH skips redirects to allow all routes
  if (isPrivateMode() && !isBothMode()) {
    if (isAppContext(to.path) || isEmbeddedContext(to.path)) {
      const path = isAppContext(to.path) ? to.path.replace('/app/', '/cb/') : to.path.replace('/int/', '/cb/');
      return next({
        path,
        query: { ...to.query },
        hash: to.hash,
      }).catch(() => {
        return next({ name: 'backoffice-home' });
      });
    }
  }
  if (isPublicMode() && !isBothMode()) {
    if (isCbContext(to.path)) {
      return next({ name: 'schedule-start' });
    }
  }

  // Prefix consistency guard (BOTH mode only):
  // Keeps subscribers in /cb context when shared components navigate using /app route names,
  // or when an authenticated user opens /app patient routes directly (new tab, refresh, copied link).
  if (isBothMode()) {
    const token = localStorage.getItem('token');
    const { valid: hasValidAuth } = isTokenExpired(token);
    const cbEquivalent = shouldRewriteAppRouteToCb({
      toPath: to.path,
      toName: to.name,
      fromPath: from.path,
      hasValidAuth: Boolean(token && hasValidAuth),
    });

    if (cbEquivalent) {
      return next({
        name: cbEquivalent,
        params: { ...to.params },
        query: { ...to.query },
      });
    }
  }

  // Auth guard for /cb routes (PRIVATE and BOTH)
  if (isPrivateMode() && isCbContext(to.path)) {
    const isPublicRoute = PRIVATE_PUBLIC_ROUTE_NAMES.includes(to.name);
    if (isPublicRoute) {
      return next();
    }

    const token = localStorage.getItem('token');
    const { expired, valid } = isTokenExpired(token);

    if (!valid || !token) {
      store.commit('plans-auth/CLEAR_LOGGED_USER');
      return next({ name: 'login', query: { sessionExpired: '1' } });
    }

    if (expired) {
      const refreshTokenValue = localStorage.getItem('refreshToken');
      const user = getUserData();

      if (!user?.username || !refreshTokenValue) {
        store.commit('plans-auth/CLEAR_LOGGED_USER');
        return next({ name: 'login', query: { sessionExpired: '1' } });
      }
      await handleRefreshToken(refreshTokenValue, user, next);
    }

    if (to.matched.length === 0) {
      return next({ name: 'backoffice-home' });
    }

    return next();
  }

  return next();
});

export default router;
